import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'

const TYPES = {
  medecin_generaliste: 'Médecin généraliste',
  medecin_specialiste: 'Médecin spécialiste',
  kine: 'Kinésithérapeute',
  osteo: 'Ostéopathe',
  dentiste: 'Dentiste',
  psychologue: 'Psychologue',
  psy: 'Psychiatre',
  podologue: 'Podologue',
  orthoptiste: 'Orthoptiste',
  dieticienne: 'Diététicienne',
  sage_femme: 'Sage-femme',
  infirmier: 'Infirmier(e)',
  acupuncteur: 'Acupuncteur',
  autre: 'Autre'
}

const TYPE_COLORS = {
  medecin_generaliste: 'badge-blue', medecin_specialiste: 'badge-blue',
  kine: 'badge-green', osteo: 'badge-green',
  dentiste: 'badge-amber', psychologue: 'badge-amber', psy: 'badge-amber',
  podologue: 'badge-gray', orthoptiste: 'badge-gray', dieticienne: 'badge-gray',
  sage_femme: 'badge-gray', infirmier: 'badge-gray', acupuncteur: 'badge-gray', autre: 'badge-gray'
}

function emptyForm() {
  return {
    nom: '', prenom: '', specialite: '', type: 'medecin_generaliste',
    // Champs PUBLICS (visibles sur fiche patient)
    telephone_cabinet: '', adresse_cabinet: '', ville: '', site_web: '', presentation: '',
    // Champs PRIVÉS (usage interne uniquement)
    telephone_direct: '', email: '', adresse: '', notes: ''
  }
}

function initiales(prenom, nom) {
  return ((prenom?.[0] || '') + (nom?.[0] || '')).toUpperCase()
}

const LABEL_PRIVE = (
  <span style={{display:'inline-flex', alignItems:'center', gap:4, fontSize:11, color:'var(--text-3)', fontWeight:400, marginLeft:6}}>
    <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="5" width="8" height="6" rx="1"/><path d="M4 5V3.5a2 2 0 014 0V5"/></svg>
    usage interne
  </span>
)

export default function Partenaires() {
  const { praticien } = useAuth()
  const [partenaires, setPartenaires] = useState([])
  const [filtered, setFiltered] = useState([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [panel, setPanel] = useState(null)
  const [form, setForm] = useState(emptyForm())
  const [saving, setSaving] = useState(false)
  const [filterType, setFilterType] = useState('all')
  const [selection, setSelection] = useState(new Set()) // IDs cochés
  const [panelEmail, setPanelEmail] = useState(false)
  const [emailPatient, setEmailPatient] = useState('')
  const [emailPrenom, setEmailPrenom] = useState('')
  const [sendingEmail, setSendingEmail] = useState(false)
  const [emailSent, setEmailSent] = useState(false)
  const [patientSelectionne, setPatientSelectionne] = useState(null)
  const [listePatients, setListePatients] = useState([])

  const patientsFiltered = emailPrenom.length >= 2
    ? listePatients.filter(p =>
        `${p.prenom} ${p.nom}`.toLowerCase().includes(emailPrenom.toLowerCase())
      ).slice(0, 8)
    : []

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase.from('praticiens_partenaires').select('*').order('nom')
    setPartenaires(data || [])
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    supabase.from('patients').select('id, prenom, nom, email').order('nom')
      .then(({ data }) => setListePatients(data || []))
  }, [])

  useEffect(() => {
    const handler = () => { setForm(emptyForm()); setPanel('new') }
    window.addEventListener('cabinet:new', handler)
    return () => window.removeEventListener('cabinet:new', handler)
  }, [])

  useEffect(() => {
    let result = partenaires
    if (filterType !== 'all') result = result.filter(p => p.type === filterType)
    if (search) result = result.filter(p =>
      `${p.prenom} ${p.nom}`.toLowerCase().includes(search.toLowerCase()) ||
      p.specialite?.toLowerCase().includes(search.toLowerCase()) ||
      p.ville?.toLowerCase().includes(search.toLowerCase())
    )
    setFiltered(result)
  }, [search, filterType, partenaires])

  async function save() {
    setSaving(true)
    const payload = { ...form, created_by: praticien.id }
    if (panel === 'new') {
      const { error } = await supabase.from('praticiens_partenaires').insert(payload)
      if (error) { alert('Erreur : ' + error.message); setSaving(false); return }
    } else {
      const { error } = await supabase.from('praticiens_partenaires').update(form).eq('id', panel.id)
      if (error) { alert('Erreur : ' + error.message); setSaving(false); return }
    }
    setSaving(false); setPanel(null); load()
  }

  async function del() {
    if (!window.confirm(`Supprimer ${panel.prenom} ${panel.nom} ?`)) return
    await supabase.from('praticiens_partenaires').delete().eq('id', panel.id)
    setPanel(null); load()
  }

  function toggleSelection(id, e) {
    e.stopPropagation()
    setSelection(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  async function sendFichesByEmail() {
    if (selection.size === 0) return
    setSendingEmail(true)
    const selectionnes = partenaires.filter(p => selection.has(p.id))

    const lignesHtml = selectionnes.map(p => {
      const titre = ['psychologue', 'sage_femme', 'kine', 'infirmier', 'dieticienne'].includes(p.type) ? '' : 'Dr '
      const nom = titre + p.prenom + ' ' + p.nom
      const spec = p.specialite || TYPES[p.type] || ''
      let coordonnees = ''
      if (p.adresse_cabinet) coordonnees += p.adresse_cabinet
      if (p.ville) coordonnees += ', ' + p.ville
      if (p.telephone_cabinet) coordonnees += ' — Tel : ' + p.telephone_cabinet
      if (p.site_web) coordonnees += ' — ' + p.site_web
      return `<tr>
        <td style="padding:8px 12px;font-weight:600;">${nom}</td>
        <td style="padding:8px 12px;color:#666;">${spec}</td>
        <td style="padding:8px 12px;color:#444;">${coordonnees}</td>
      </tr>`
    }).join('')

    const html = `
      <p>Bonjour${patientSelectionne ? ' ' + patientSelectionne.prenom : emailPrenom ? ' ' + emailPrenom : ''},</p>
      <p>Suite à votre consultation, voici les coordonnées des praticiens vers qui je vous oriente :</p>
      <table style="border-collapse:collapse;width:100%;font-family:sans-serif;font-size:14px;">
        <thead>
          <tr style="background:#f5f5f5;">
            <th style="padding:8px 12px;text-align:left;">Praticien</th>
            <th style="padding:8px 12px;text-align:left;">Spécialité</th>
            <th style="padding:8px 12px;text-align:left;">Coordonnées</th>
          </tr>
        </thead>
        <tbody>${lignesHtml}</tbody>
      </table>
      <p style="margin-top:20px;">N'hésitez pas à me contacter si vous avez des questions.</p>
      <p>Cordialement,<br/><strong>${praticien.prenom} ${praticien.nom}</strong><br/>Cabinet Ostéopathie</p>
    `

    try {
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
      const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY
      const res = await fetch(supabaseUrl + '/functions/v1/send-orientations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + supabaseKey,
          'apikey': supabaseKey
        },
        body: JSON.stringify({
          emailPatient,
          prenomPatient: patientSelectionne?.prenom || emailPrenom,
          praticiens: partenaires.filter(p => selection.has(p.id)),
          praticienNom: praticien.nom,
          praticienPrenom: praticien.prenom
        })
      })
      if (res.ok) {
        setEmailSent(true)
        setTimeout(() => { setEmailSent(false); setPanelEmail(false); setSelection(new Set()); setEmailPatient(''); setEmailPrenom(''); setPatientSelectionne(null) }, 3000)
      } else {
        const err = await res.json()
        alert('Erreur : ' + JSON.stringify(err.error || err))
      }
    } catch (e) {
      alert('Erreur réseau — vérifiez votre connexion')
    }
    setSendingEmail(false)
  }

  return (
    <>
      <div style={{display:'flex', gap:10, marginBottom:16, flexWrap:'wrap', alignItems:'center'}}>
        <select className="btn btn-sm" value={filterType} onChange={e => setFilterType(e.target.value)}
          style={{border:'0.5px solid var(--border-md)', fontWeight: filterType !== 'all' ? 500 : 400}}>
          <option value="all">Tous les praticiens</option>
          <optgroup label="Médecins">
            <option value="medecin_generaliste">Médecin généraliste</option>
            <option value="medecin_specialiste">Médecin spécialiste</option>
            <option value="psy">Psychiatre</option>
          </optgroup>
          <optgroup label="Thérapies manuelles">
            <option value="kine">Kinésithérapeute</option>
            <option value="osteo">Ostéopathe</option>
            <option value="acupuncteur">Acupuncteur</option>
            <option value="podologue">Podologue</option>
          </optgroup>
          <optgroup label="Autres professionnels">
            <option value="dentiste">Dentiste</option>
            <option value="psychologue">Psychologue</option>
            <option value="orthoptiste">Orthoptiste</option>
            <option value="dieticienne">Diététicienne</option>
            <option value="sage_femme">Sage-femme</option>
            <option value="infirmier">Infirmier(e)</option>
            <option value="autre">Autre</option>
          </optgroup>
        </select>
        <span style={{fontSize:12, color:'var(--text-3)'}}>
          {filtered.length} praticien{filtered.length > 1 ? 's' : ''}
        </span>
      </div>

      <div className="search-bar">
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="6.5" cy="6.5" r="4.5"/><path d="M10.5 10.5l3 3"/></svg>
        <input placeholder="Rechercher par nom, spécialité, ville…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {loading ? <div className="empty">Chargement…</div> : (
        <>
          {/* Barre d'action sélection */}
          {selection.size > 0 && (
            <div style={{display:'flex', alignItems:'center', gap:12, padding:'10px 16px', background:'#EAF3DE', borderRadius:'var(--radius)', marginBottom:12, border:'1px solid #639922'}}>
              <span style={{fontSize:13, fontWeight:500, color:'#27500A'}}>
                {selection.size} praticien{selection.size > 1 ? 's' : ''} sélectionné{selection.size > 1 ? 's' : ''}
              </span>
              <button className="btn btn-sm" style={{background:'#1D9E75', color:'#fff', border:'none', fontWeight:500}}
                onClick={() => setPanelEmail(true)}>
                ✉ Envoyer les coordonnées par mail
              </button>
              <button className="btn btn-sm" style={{marginLeft:'auto'}}
                onClick={() => setSelection(new Set())}>
                Tout décocher
              </button>
            </div>
          )}

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{width:36}}></th>
                  <th>Praticien</th><th>Spécialité</th><th>Type</th><th>Tél. cabinet</th><th>Ville</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && <tr><td colSpan={6} className="empty">Aucun partenaire</td></tr>}
                {filtered.map(p => (
                  <tr key={p.id} className="clickable" onClick={() => { setForm({
                    nom: p.nom||'', prenom: p.prenom||'', specialite: p.specialite||'', type: p.type||'medecin_generaliste',
                    telephone_cabinet: p.telephone_cabinet||'', adresse_cabinet: p.adresse_cabinet||'',
                    ville: p.ville||'', site_web: p.site_web||'', presentation: p.presentation||'',
                    telephone_direct: p.telephone_direct||'', email: p.email||'',
                    adresse: p.adresse||'', notes: p.notes||''
                  }); setPanel(p) }}>
                    <td onClick={e => toggleSelection(p.id, e)} style={{textAlign:'center'}}>
                      <input type="checkbox" checked={selection.has(p.id)} onChange={() => {}}
                        style={{width:15, height:15, cursor:'pointer', accentColor:'#1D9E75'}} />
                    </td>
                    <td>
                      <div className="cell-name">
                        <div className="avatar">{initiales(p.prenom, p.nom)}</div>
                        {p.type !== 'psychologue' ? 'Dr ' : ''}{p.prenom} {p.nom}
                      </div>
                    </td>
                    <td className="td-muted">{p.specialite || TYPES[p.type] || '—'}</td>
                    <td><span className={`badge ${TYPE_COLORS[p.type] || 'badge-gray'}`}>{TYPES[p.type] || p.type}</span></td>
                    <td className="td-muted">{p.telephone_cabinet || '—'}</td>
                    <td className="td-muted">{p.ville || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* PANEL ENVOI EMAIL */}
      {panelEmail && (
        <>
          <div className="panel-overlay" onClick={() => setPanelEmail(false)} />
          <div className="panel" style={{width:480}}>
            <div className="panel-header">
              <h3>Envoyer les coordonnées au patient</h3>
              <button className="close-btn" onClick={() => setPanelEmail(false)}>✕</button>
            </div>
            <div className="panel-body">
              {/* Récap praticiens sélectionnés */}
              <div style={{background:'var(--bg)', borderRadius:'var(--radius)', padding:'10px 14px', marginBottom:16}}>
                <div style={{fontSize:12, fontWeight:500, color:'var(--text-2)', marginBottom:8}}>
                  Praticiens inclus dans le mail :
                </div>
                {partenaires.filter(p => selection.has(p.id)).map(p => (
                  <div key={p.id} style={{display:'flex', alignItems:'center', gap:8, padding:'4px 0', borderBottom:'0.5px solid var(--border)'}}>
                    <span style={{fontSize:13, fontWeight:500}}>
                      {p.type !== 'psychologue' ? 'Dr ' : ''}{p.prenom} {p.nom}
                    </span>
                    <span style={{fontSize:12, color:'var(--text-3)'}}>{p.specialite || TYPES[p.type]}</span>
                    <button style={{marginLeft:'auto', background:'none', border:'none', cursor:'pointer', color:'var(--text-3)', fontSize:14}}
                      onClick={() => setSelection(prev => { const n = new Set(prev); n.delete(p.id); return n })}>
                      ✕
                    </button>
                  </div>
                ))}
              </div>

              {/* Recherche patient */}
              <div className="form-group">
                <label>Rechercher le patient</label>
                <input
                  placeholder="Nom ou prénom…"
                  value={emailPrenom}
                  onChange={e => {
                    setEmailPrenom(e.target.value)
                    setEmailPatient('')
                    setPatientSelectionne(null)
                  }}
                  autoFocus
                />
              </div>

              {/* Résultats de recherche */}
              {emailPrenom.length >= 2 && !patientSelectionne && (
                <div style={{border:'0.5px solid var(--border-md)', borderRadius:'var(--radius)', overflow:'hidden', marginBottom:14}}>
                  {patientsFiltered.length === 0 && (
                    <div style={{padding:'10px 14px', fontSize:13, color:'var(--text-3)'}}>Aucun patient trouvé</div>
                  )}
                  {patientsFiltered.map(p => (
                    <div key={p.id}
                      onClick={() => {
                        setPatientSelectionne(p)
                        setEmailPrenom(p.prenom)
                        setEmailPatient(p.email || '')
                      }}
                      style={{padding:'10px 14px', cursor:'pointer', borderBottom:'0.5px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center'}}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}>
                      <span style={{fontSize:13, fontWeight:500}}>{p.prenom} {p.nom}</span>
                      <span style={{fontSize:12, color: p.email ? 'var(--text-2)' : 'var(--red-text)'}}>
                        {p.email || 'Pas de mail'}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Patient sélectionné */}
              {patientSelectionne && (
                <div style={{display:'flex', alignItems:'center', gap:10, padding:'10px 14px', background:'var(--bg)', borderRadius:'var(--radius)', marginBottom:14, border:'1px solid var(--border-md)'}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13, fontWeight:600}}>{patientSelectionne.prenom} {patientSelectionne.nom}</div>
                    <div style={{fontSize:12, color: emailPatient ? 'var(--text-2)' : 'var(--red-text)', marginTop:2}}>
                      {emailPatient || 'Aucun email enregistré — saisissez-le ci-dessous'}
                    </div>
                  </div>
                  <button style={{background:'none', border:'none', cursor:'pointer', color:'var(--text-3)'}}
                    onClick={() => { setPatientSelectionne(null); setEmailPrenom(''); setEmailPatient('') }}>✕</button>
                </div>
              )}

              {/* Si pas d'email, permettre la saisie manuelle */}
              {patientSelectionne && !emailPatient && (
                <div className="form-group">
                  <label>Email du patient (non renseigné dans sa fiche)</label>
                  <input type="email" value={emailPatient} onChange={e => setEmailPatient(e.target.value)} placeholder="saisir l'email manuellement" />
                </div>
              )}
            </div>
            <div className="panel-footer">
              <button className="btn btn-sm" onClick={() => setPanelEmail(false)}>Annuler</button>
              <button className="btn btn-primary btn-sm"
                style={{background: emailSent ? '#1D9E75' : '', border: emailSent ? 'none' : ''}}
                onClick={sendFichesByEmail}
                disabled={sendingEmail || !emailPatient || selection.size === 0}>
                {sendingEmail ? 'Envoi…' : emailSent ? '✓ Envoyé !' : '✉ Envoyer'}
              </button>
            </div>
          </div>
        </>
      )}

      {panel && (
        <>
          <div className="panel-overlay" onClick={() => setPanel(null)} />
          <div className="panel" style={{width:620, maxWidth:'calc(100vw - 32px)'}}>
            <div className="panel-header">
              <h3>{panel === 'new' ? 'Nouveau partenaire' : (panel.type !== 'psychologue' ? 'Dr ' : '') + panel.prenom + ' ' + panel.nom}</h3>
              <button className="close-btn" onClick={() => setPanel(null)}>✕</button>
            </div>
            <div className="panel-body">

              {/* Identité */}
              <div className="form-row">
                <div className="form-group"><label>Prénom *</label><input value={form.prenom} onChange={e => setForm({...form, prenom: e.target.value})} /></div>
                <div className="form-group"><label>Nom *</label><input value={form.nom} onChange={e => setForm({...form, nom: e.target.value})} /></div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Type</label>
                  <select value={form.type} onChange={e => setForm({...form, type: e.target.value})}>
                    {Object.entries(TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Spécialité précise</label><input value={form.specialite} onChange={e => setForm({...form, specialite: e.target.value})} placeholder="Ex: Cardiologue, Rhumatologue…" /></div>
              </div>

              {/* Séparateur PUBLIC */}
              <div style={{margin:'16px 0 12px', padding:'8px 12px', background:'#F0FBF6', borderRadius:6, borderLeft:'3px solid #1D9E75'}}>
                <div style={{fontSize:11, fontWeight:600, color:'#1D9E75', textTransform:'uppercase', letterSpacing:'.05em'}}>
                  📋 Informations publiques — visibles sur la fiche patient
                </div>
              </div>

              <div className="form-group" style={{marginBottom:14}}>
                <label>Présentation courte</label>
                <textarea value={form.presentation} onChange={e => setForm({...form, presentation: e.target.value})}
                  placeholder="Ex: Spécialisé en rhumatologie pédiatrique, accepte les nouveaux patients…"
                  style={{minHeight:60, resize:'vertical'}} />
              </div>
              <div className="form-group" style={{marginBottom:14}}>
                <label>Adresse du cabinet</label>
                <input value={form.adresse_cabinet} onChange={e => setForm({...form, adresse_cabinet: e.target.value})} />
              </div>
              <div className="form-row">
                <div className="form-group"><label>Ville</label><input value={form.ville} onChange={e => setForm({...form, ville: e.target.value})} /></div>
                <div className="form-group"><label>Téléphone cabinet</label><input value={form.telephone_cabinet} onChange={e => setForm({...form, telephone_cabinet: e.target.value})} /></div>
              </div>
              <div className="form-group" style={{marginBottom:14}}>
                <label>Site web</label>
                <input value={form.site_web} onChange={e => setForm({...form, site_web: e.target.value})} placeholder="https://…" />
              </div>

              {/* Séparateur PRIVÉ */}
              <div style={{margin:'16px 0 12px', padding:'8px 12px', background:'var(--bg)', borderRadius:6, borderLeft:'3px solid var(--border-md)'}}>
                <div style={{fontSize:11, fontWeight:600, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'.05em', display:'flex', alignItems:'center', gap:6}}>
                  <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="5" width="8" height="6" rx="1"/><path d="M4 5V3.5a2 2 0 014 0V5"/></svg>
                  Informations internes — jamais transmises au patient
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Téléphone direct {LABEL_PRIVE}</label>
                  <input value={form.telephone_direct} onChange={e => setForm({...form, telephone_direct: e.target.value})} placeholder="Ligne directe, portable pro…" />
                </div>
                <div className="form-group">
                  <label>Email {LABEL_PRIVE}</label>
                  <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
                </div>
              </div>
              <div className="form-group">
                <label>Notes internes {LABEL_PRIVE}</label>
                <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
                  placeholder="Retours d'expérience, préférences, délais de rendez-vous…"
                  style={{minHeight:70, resize:'vertical'}} />
              </div>

            </div>
            <div className="panel-footer">
              {panel !== 'new' && <button className="btn btn-danger btn-sm" onClick={del}>Supprimer</button>}
              <div style={{flex:1}} />
              <button className="btn btn-sm" onClick={() => setPanel(null)}>Annuler</button>
              <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || !form.nom}>
                {saving ? 'Enregistrement…' : panel === 'new' ? 'Ajouter' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  )
}
